import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import Immutable from 'immutable';
import { Button, PopoverHeader, Popover, PopoverBody } from 'reactstrap';
import { connect } from 'react-redux';
import {
  changeStepBookingOnRoute,
  deselectSeat,
  updateRideBookingTotalPrice,
} from '../../../../actions/index';
import {
  CIVA_SALES_ON_ROUTE_BOOKING_PASSENGERS_STEP_INDEX,
  CIVA_SALES_ON_ROUTE_BOOKING_STEPS,
} from '../../../../config/constants';
import { DATE_TIME_FORMAT } from '../../../../config/locale';
import { numberFormatter } from '../../../../utils/number';
import Badge from '../../../common/Badge';
import { tzNormalizeDate } from '../../../../utils/date';

const RideBookingSelectedSeats = ({
  seatReservations,
  allSeatsSelected,
  dispatchChangeStepBookingOnRoute,
  dispatchDeselectSeat,
  seatSelectionChangesInProgress,
  dispatchUpdateRideBookingTotalPrice
}) => {
  const [popoverOpenList, setPopoverOpenList] = useState([]);

  const total = useMemo(
    () => seatReservations.reduce((sum, s) => sum + (s.listPrice || 0), 0),
    [seatReservations]
  );

  useEffect(() => {
    const newPopoverOpenList = seatReservations.map(() => false);

    if (newPopoverOpenList.length !== popoverOpenList.length) {
      setPopoverOpenList(newPopoverOpenList);
    }
  }, [seatReservations.length]);

  const toggle = (index) => {
    const updatedList = [...popoverOpenList];
    updatedList[index] = !updatedList[index];
    setPopoverOpenList(updatedList);
  };

  const openPopover = (index) => {
    const updatedList = [...popoverOpenList];
    updatedList[index] = true;
    setPopoverOpenList(updatedList);
  };

  const closePopover = (index) => {
    const updatedList = [...popoverOpenList];
    updatedList[index] = false;
    setPopoverOpenList(updatedList);
  };

  const handleNextButtonClick = () => {
    if (total <= 0) return;
    dispatchUpdateRideBookingTotalPrice(total);
    const passengersStep =
      CIVA_SALES_ON_ROUTE_BOOKING_STEPS[CIVA_SALES_ON_ROUTE_BOOKING_PASSENGERS_STEP_INDEX];
    dispatchChangeStepBookingOnRoute(passengersStep);
  };

  const renderSeats = () => {
    if (!seatReservations.length) {
      return (
        <span className="text-muted">
          <i>No hay asientos seleccionados</i>
        </span>
      );
    }

    const items = seatReservations.map((seatReservation, index) => {
      const {
        seat: { seatNumber },
        listPrice,
        id: seatReservationId,
        seatId,
        itineraryId,
        itinerary: {
          originName,
          destinationName,
          departureTime,
          arrivalTime,
          serviceName,
        },
      } = seatReservation;

      const formattedDepartureTime = tzNormalizeDate({
        date: departureTime,
        format: DATE_TIME_FORMAT,
      });

      const formattedArrivalTime = tzNormalizeDate({
        date: arrivalTime,
        format: DATE_TIME_FORMAT,
      });

      const amount = listPrice ? numberFormatter({ value: listPrice }) : null;

      let deselectSeatFunction = null;

      if (seatSelectionChangesInProgress.size === 0) {
        deselectSeatFunction = () =>
          dispatchDeselectSeat({
            seatReservationId,
            itineraryId,
            seatId,
            isRideBooking: true,
          });
      }

      const item = (
        <li className="list-group-item p-0" key={`td_${index.toString()}`}>
          <ul className="list-group list-group-horizontal">
            <li
              className="list-group-item border-0 pl-1 pr-1 pt-2 pb-2 text-center w-100">
              <Badge
                className="colorPurple clickable"
                onFocus={() => openPopover(index)}
                onBlur={() => closePopover(index)}
                onMouseEnter={() => openPopover(index)}
                onMouseLeave={() => closePopover(index)}
                onClick={() => openPopover(index)}
                id={`pp_${index}`}
                role="button"
                text={seatNumber}
              />
              <Popover
                placement="bottom"
                isOpen={popoverOpenList[index]}
                target={`pp_${index}`}
                toggle={() => toggle(index)}
              >
                <PopoverHeader>{serviceName}</PopoverHeader>
                <PopoverBody>
                  <p>
                    Origen: {originName}
                    <br />
                    Salida: {formattedDepartureTime}
                  </p>
                  <p>
                    Destino: {destinationName}
                    <br />
                    Llegada: {formattedArrivalTime}
                  </p>
                </PopoverBody>
              </Popover>
            </li>
            <li className="list-group-item border-0 pl-1 pr-1 pt-2 pb-2 text-right w60p">
              {amount}
            </li>
            <li className="list-group-item border-0 pl-1 pr-1 pt-2 pb-2 text-right w20p">
              <i
                className="fa fa-times-circle text-danger clickable"
                role="button"
                onClick={deselectSeatFunction}
                onKeyPress={deselectSeatFunction}
                tabIndex={0}
              />
            </li>
          </ul>
        </li>
      );

      return item;
    });

    let footer = null;

    if (total !== 0) {
      footer = (
        <li className="list-group-item p-0" key="total">
          <ul className="list-group list-group-horizontal">
            <li className="list-group-item border-0 pl-1 pr-1 pt-2 pb-2 w20p">
              &nbsp;
            </li>
            <li className="list-group-item border-0 pl-1 pr-1 pt-2 pb-2 w60p text-right font-weight-bold">
              TOTAL: {numberFormatter({ value: total })}
            </li>
            <li className="list-group-item border-0 pl-1 pr-1 pt-2 pb-2 w20p">
              &nbsp;
            </li>
          </ul>
        </li>
      );
    }

    return (
      <ul className="list-group list-group-flush">
        {items}
        {footer}
      </ul>
    );
  };

  const renderNextButton = () => (
    <Button
      className="mt-3"
      disabled={!allSeatsSelected}
      color="primary"
      onClick={handleNextButtonClick}
      size="lg"
    >
      Siguiente <i className="fa fa-arrow-circle-right" />
    </Button>
  );

  return (
    <div className="mt-4">
      <div className="card">
        <div className="card-body p-3">
          <h5 className="card-title">Asientos</h5>
          <div className="card-text">{renderSeats()}</div>
          <div
            className='text-center'
          >
            {renderNextButton()}
          </div>
        </div>
      </div>
    </div>
  );
};

RideBookingSelectedSeats.propTypes = {
  seatReservations: PropTypes.arrayOf(PropTypes.shape({})),
  allSeatsSelected: PropTypes.bool.isRequired,
  dispatchChangeStepBookingOnRoute: PropTypes.func.isRequired,
  dispatchDeselectSeat: PropTypes.func.isRequired,
  seatSelectionChangesInProgress: PropTypes.instanceOf(Immutable.Set)
    .isRequired,
  dispatchUpdateRideBookingTotalPrice: PropTypes.func.isRequired
};

RideBookingSelectedSeats.defaultProps = {
  seatReservations: [],
};

const mapStateToProps = ({ BookingUnit, authentication }) => {
  const numPassengers = BookingUnit.Booking.getIn([
    'search',
    'query',
    'numPassengers',
  ]);

  const seatReservations = BookingUnit.Booking.getIn([
    'seats',
    'seatReservations',
  ]).toJS();

  const seatSelectionChangesInProgress = BookingUnit.Booking.getIn([
    'seats',
    'seatSelectionChangesInProgress',
  ]);

  let allSeatsSelected = false;

  if (
    !numPassengers &&
    seatReservations.length >= 1 &&
    !seatSelectionChangesInProgress.size
  ) {
    allSeatsSelected = true;
  } else if (
    numPassengers &&
    parseInt(numPassengers, 10) === seatReservations.length &&
    !seatSelectionChangesInProgress.size
  ) {
    allSeatsSelected = true;
  }

  return {
    seatReservations,
    allSeatsSelected,
    seatSelectionChangesInProgress,
    isForMine: !!authentication.get('user').salesSessionUserId,
  };
};

const mapDispatchToProps = {
  dispatchChangeStepBookingOnRoute: changeStepBookingOnRoute,
  dispatchDeselectSeat: deselectSeat,
  dispatchUpdateRideBookingTotalPrice: updateRideBookingTotalPrice
};

export default connect(mapStateToProps, mapDispatchToProps)(RideBookingSelectedSeats);